/**
 * A sample programme: the topics of a subject over the year, in order (simplified НУШ). The demo
 * journal is built from it (each topic spans an equal part of the year), and a teacher may start
 * planning from it. Keyed by the subject's short key; `SUBJECT_KEYS` maps school subject names.
 */
export const TOPICS: Readonly<Record<string, readonly string[]>> = {
  ukr: [
    'Повторення вивченого. Текст і його ознаки',
    'Звуки і букви. Фонетика',
    'Будова слова. Корінь, префікс, суфікс',
    'Іменник як частина мови',
    'Прикметник: рід, число, відмінок',
    'Дієслово. Часи дієслова',
    'Речення. Головні та другорядні члени',
    'Пряма мова і діалог',
    'Розвиток мовлення. Твір-опис',
    'Підсумкове повторення',
  ],
  ukr_lit: [
    'Усна народна творчість',
    'Казки та легенди українського народу',
    'Тарас Шевченко. Поезії',
    'Леся Українка. Лірика',
    'Іван Франко. Оповідання',
    'Михайло Коцюбинський. Новели',
    'Сучасна українська література',
    'Література рідного краю',
  ],
  world_lit: [
    'Міфи народів світу',
    'Казки братів Грімм і Ш. Перро',
    'Г. К. Андерсен. «Снігова королева»',
    'Ж. Верн. Пригодницька література',
    'Марк Твен. «Пригоди Тома Сойєра»',
    'Поезія природи',
    'Фантастика і фентезі',
    'Сучасна література для підлітків',
  ],
  eng: [
    'Hello! Introductions and greetings',
    'My family and friends',
    'School life and timetable',
    'Daily routines. Present Simple',
    'Food and healthy eating',
    'Travelling. Past Simple',
    'Sports and hobbies',
    'Holidays and traditions',
    'The world around us',
    'Revision and project work',
  ],
  math: [
    'Натуральні числа. Повторення',
    'Додавання і віднімання багатоцифрових чисел',
    'Множення і ділення',
    'Задачі на рух',
    'Звичайні дроби',
    'Десяткові дроби',
    'Відсотки',
    'Геометричні фігури. Периметр і площа',
    'Рівняння',
    'Підсумкове повторення',
  ],
  algebra: [
    'Цілі вирази. Тотожності',
    'Степінь з натуральним показником',
    'Одночлени і многочлени',
    'Формули скороченого множення',
    'Розкладання многочленів на множники',
    'Функції. Лінійна функція',
    'Лінійні рівняння з однією змінною',
    'Системи лінійних рівнянь',
  ],
  geometry: [
    'Найпростіші геометричні фігури',
    'Взаємне розміщення прямих на площині',
    'Трикутники. Ознаки рівності',
    'Рівнобедрений трикутник',
    'Паралельні прямі',
    'Сума кутів трикутника',
    'Коло і круг',
    'Геометричні побудови',
  ],
  calculus: [
    'Числові функції та їх властивості',
    'Степенева функція',
    'Корінь n-го степеня',
    'Тригонометричні функції',
    'Тригонометричні рівняння',
    'Похідна та її застосування',
    'Показникова функція',
    'Логарифми',
  ],
  explore: [
    'Я — школяр. Мій клас',
    'Осінь у природі',
    'Моя родина',
    'Рослини навколо нас',
    'Тварини рідного краю',
    'Безпека вдома і на вулиці',
    'Моя Україна',
    'Весна і праця людей',
  ],
  design: [
    'Аплікація з природних матеріалів',
    'Конструювання з паперу',
    'Ліплення з пластиліну',
    'Виготовлення листівки',
    'Робота з тканиною',
    'Новорічна прикраса',
    'Модель транспорту',
    'Проєкт «Подарунок мамі»',
  ],
  nature: [
    'Природа навколо нас',
    'Речовини і тіла',
    'Світло і звук',
    'Земля — наша планета',
    'Погода і клімат',
    'Рослини і тварини',
    'Людина і довкілля',
    'Дослідницький проєкт',
  ],
  history: [
    'Історія як наука',
    'Первісні люди на території України',
    'Стародавній Єгипет',
    'Стародавня Греція',
    'Стародавній Рим',
    'Київська держава',
    'Козацтво',
    'Моя мала батьківщина',
  ],
  history_ua: [
    'Русь-Україна: становлення держави',
    'Князювання Володимира Великого',
    'Ярослав Мудрий',
    'Королівство Руське',
    'Литовсько-руська доба',
    'Українське козацтво',
    'Національна революція 1648–1676 рр.',
    'Гетьманщина',
  ],
  history_world: [
    'Середньовічна Європа',
    'Візантійська імперія',
    'Арабський світ',
    'Хрестові походи',
    'Відродження',
    'Великі географічні відкриття',
    'Реформація',
    'Новий час',
  ],
  biology: [
    'Біологія — наука про життя',
    'Клітина',
    'Тканини і органи',
    'Різноманітність рослин',
    'Різноманітність тварин',
    'Гриби і бактерії',
    'Екосистеми',
    'Біологія людини',
  ],
  biology_eco: [
    'Рівні організації життя',
    'Обмін речовин',
    'Генетика і спадковість',
    'Мінливість',
    'Еволюція',
    'Екологія популяцій',
    'Біосфера',
    'Сталий розвиток',
  ],
  geography: [
    'Географічна карта',
    'Земля у Всесвіті',
    'Літосфера. Рельєф',
    'Атмосфера. Погода',
    'Гідросфера',
    'Природні зони',
    'Населення світу',
    'Географія України',
  ],
  physics: [
    'Фізика як наука',
    'Механічний рух',
    'Швидкість. Графіки руху',
    'Взаємодія тіл. Сила',
    'Тиск. Закон Паскаля',
    'Робота і енергія',
    'Теплові явища',
    'Лабораторний практикум',
  ],
  chemistry: [
    'Хімія як наука. Безпека в кабінеті',
    'Речовини та їх властивості',
    'Атоми і хімічні елементи',
    'Періодична система',
    'Хімічні формули',
    'Хімічні реакції',
    'Оксиди, кислоти, основи',
    'Розчини',
  ],
  informatics: [
    'Безпека в інтернеті',
    'Інформація і повідомлення',
    'Файли і папки',
    'Текстовий редактор',
    'Презентації',
    'Алгоритми і виконавці',
    'Програмування у Scratch',
    'Проєкт: власна гра',
  ],
  art: [
    'Колір і настрій',
    'Пейзаж',
    'Портрет',
    'Натюрморт',
    'Українське народне мистецтво',
    'Музика і театр',
    'Дизайн навколо нас',
    'Творчий проєкт',
  ],
  pe: [
    'Легка атлетика. Біг',
    'Стрибки у довжину',
    'Рухливі ігри',
    'Баскетбол',
    'Волейбол',
    'Гімнастика',
    'Футбол',
    'Фізична підготовка. Тести',
  ],
  technology: [
    'Техніка безпеки в майстерні',
    'Матеріали і інструменти',
    'Проєктування виробу',
    'Обробка деревини',
    'Ручне шиття',
    'Кулінарія',
    'Дизайн інтер’єру',
    'Захист проєкту',
  ],
  health: [
    'Здоров’я людини',
    'Режим дня',
    'Харчування',
    'Безпека на дорозі',
    'Мінна безпека',
    'Перша допомога',
    'Емоції і стосунки',
    'Цифрова безпека',
  ],
  defense: [
    'Збройні Сили України',
    'Статути ЗСУ',
    'Стройова підготовка',
    'Домедична допомога',
    'Цивільний захист',
    'Мінна безпека',
    'Топографія',
    'Інформаційна безпека',
  ],
};

/** The school's subject names → the keys of the sample programme. */
export const SUBJECT_KEYS: Readonly<Record<string, string>> = {
  'Українська мова': 'ukr',
  'Українська література': 'ukr_lit',
  'Зарубіжна література': 'world_lit',
  'Англійська мова': 'eng',
  Математика: 'math',
  Алгебра: 'algebra',
  Геометрія: 'geometry',
  'Алгебра і початки аналізу': 'calculus',
  'Я досліджую світ': 'explore',
  'Дизайн і технології': 'design',
  'Пізнаємо природу': 'nature',
  Історія: 'history',
  'Історія України': 'history_ua',
  'Всесвітня історія': 'history_world',
  Біологія: 'biology',
  'Біологія і екологія': 'biology_eco',
  Географія: 'geography',
  Фізика: 'physics',
  Хімія: 'chemistry',
  Інформатика: 'informatics',
  Мистецтво: 'art',
  'Фізична культура': 'pe',
  Технології: 'technology',
  'Здоров’я, безпека та добробут': 'health',
  'Захист України': 'defense',
};

/** The sample programme of a subject by its school name, or nothing. */
export function programTopics(subject: string): readonly string[] {
  const key = SUBJECT_KEYS[subject];
  return key ? (TOPICS[key] ?? []) : [];
}
